<template>
  <div class="as-root">

    <!-- Header -->
    <header class="as-header">
      <button class="as-back-btn" @click="$router.back()" title="Voltar">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        <span>Voltar</span>
      </button>

      <div class="as-header-brand">
        <span class="as-brand-name">Minha Academia</span>
      </div>

      <div class="as-header-progress" v-if="modules.length">
        <span class="as-progress-text">{{ completedCount }} / {{ totalLessons }} aulas</span>
        <div class="as-progress-track">
          <div class="as-progress-fill" :style="{ width: overallPct + '%' }"></div>
        </div>
        <span class="as-progress-pct">{{ overallPct }}%</span>
      </div>
      <div v-else class="as-header-progress"></div>

      <button class="as-menu-toggle" @click="sidebarOpen = !sidebarOpen" aria-label="Menu">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
    </header>

    <div v-if="isAdminPreview" class="as-preview-banner">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      Modo pré-visualização — você está vendo como seus alunos verão o treinamento
      <button class="as-preview-manage" @click="$router.push({ name: 'app-treinamentos' })">Gerenciar conteúdo</button>
    </div>

    <div class="as-body">
      <div v-if="sidebarOpen" class="as-overlay" @click="sidebarOpen = false"></div>

      <!-- Sidebar -->
      <aside class="as-sidebar" :class="{ 'as-sidebar--open': sidebarOpen }">
        <div class="as-sidebar-inner">
          <div v-if="loading" class="as-sidebar-loading">Carregando...</div>
          <div v-else-if="!modules.length" class="as-sidebar-empty">Nenhum conteúdo disponível.</div>
          <div v-for="m in modules" :key="m.id" class="as-mod">
            <button class="as-mod-hd" @click="toggleModule(m.id)">
              <svg class="as-chevron" :class="{ 'as-chevron--open': expandedModules[m.id] }"
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M9 18l6-6-6-6"/>
              </svg>
              <span class="as-mod-name">{{ m.title }}</span>
              <span class="as-mod-pct" :class="{ 'as-mod-pct--done': moduleCompletePct(m) >= 100 }">
                {{ moduleCompletePct(m) }}%
              </span>
            </button>
            <div v-show="expandedModules[m.id]" class="as-lesson-list">
              <button
                v-for="l in m.lessons"
                :key="l.id"
                class="as-lesson-btn"
                :class="{
                  'as-lesson-btn--active': currentLesson && currentLesson.id === l.id,
                  'as-lesson-btn--done': isDone(l.id),
                }"
                @click="goLesson(l, m)"
              >
                <span class="as-lesson-ico">
                  <svg v-if="isDone(l.id)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <svg v-else-if="lessonPct(l.id) > 0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>
                  </svg>
                  <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <circle cx="12" cy="12" r="9"/>
                  </svg>
                </span>
                <span class="as-lesson-name">{{ l.title }}</span>
                <span v-if="l.duration_min" class="as-lesson-dur">{{ l.duration_min }}m</span>
                <button
                  class="as-lesson-flag"
                  :class="{ 'as-lesson-flag--on': isDone(l.id) }"
                  @click.stop="toggleLessonDone(l)"
                  :title="isDone(l.id) ? 'Desmarcar aula assistida' : 'Marcar aula assistida'"
                >
                  {{ isDone(l.id) ? 'Assistida' : 'Marcar' }}
                </button>
              </button>
            </div>
          </div>
        </div>
      </aside>

      <!-- Main content -->
      <main class="as-main" @click="sidebarOpen = false">

        <div v-if="loading" class="as-state">
          <div class="as-spinner"></div>
          <p>Carregando seu treinamento...</p>
        </div>

        <div v-else-if="!currentLesson" class="as-welcome">
          <div class="as-welcome-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#E86A10" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 19.5A2.5 2.5 0 016.5 17H20M4 4.5A2.5 2.5 0 016.5 7H20M6.5 7v10"/>
            </svg>
          </div>
          <h1>Bem-vindo ao treinamento!</h1>
          <p>Selecione uma aula no menu ao lado para começar.</p>
          <button v-if="firstLesson" class="as-start-btn" @click="goLesson(firstLesson.l, firstLesson.m)">
            Começar agora
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <template v-else>
          <div class="as-lesson-wrap">
            <div class="as-lesson-head">
              <div class="as-breadcrumb">
                <span>{{ currentModule && currentModule.title }}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
                <span>{{ currentLesson.title }}</span>
              </div>
              <h1 class="as-lesson-title">{{ currentLesson.title }}</h1>
              <div class="as-lesson-badges">
                <span v-if="currentLesson.duration_min" class="as-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  {{ currentLesson.duration_min }} min
                </span>
                <span class="as-badge as-badge--progress">{{ Math.round(lessonPct(currentLesson.id)) }}% assistido</span>
                <span v-if="isDone(currentLesson.id)" class="as-badge as-badge--done">✓ Concluído</span>
              </div>
            </div>

            <div class="as-player-wrap">
              <iframe
                ref="lessonFrame"
                class="as-player"
                :srcdoc="buildEmbed(currentLesson.id_vsl)"
                allowfullscreen
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            </div>

            <div v-if="currentLesson.description" class="as-lesson-desc">
              <p>{{ currentLesson.description }}</p>
            </div>

            <div class="as-rating-card">
              <div class="as-rating-label">Como você avalia esta aula?</div>
              <div class="as-stars">
                <button
                  v-for="s in 5"
                  :key="s"
                  class="as-star"
                  :class="{ 'as-star--on': s <= displayRating }"
                  @mouseenter="hoverRating = s"
                  @mouseleave="hoverRating = 0"
                  @click="submitRating(s)"
                  :aria-label="`Avaliar ${s} estrelas`"
                >★</button>
              </div>
              <transition name="as-fade">
                <span v-if="ratingSaved" class="as-rating-saved">Avaliação salva!</span>
              </transition>
            </div>

            <div class="as-nav">
              <button v-if="prevLesson" class="as-nav-btn" @click="goLesson(prevLesson.l, prevLesson.m)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                Anterior
              </button>
              <div v-else></div>
              <button v-if="nextLesson" class="as-nav-btn as-nav-btn--primary" @click="goLesson(nextLesson.l, nextLesson.m)">
                Próxima aula
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </div>

            <div class="as-manual-complete">
              <button class="as-manual-complete__btn" @click="toggleLessonDone(currentLesson)">
                {{ isDone(currentLesson.id) ? 'Desmarcar aula como assistida' : 'Marcar aula como assistida' }}
              </button>
            </div>
          </div>
        </template>

      </main>
    </div>
  </div>
</template>

<script>
import api from '@/libs/axios'

const publicApi = api

export default {
  name: 'MyAcademy',
  data() {
    return {
      loading: true,
      ownerUserId: null,
      studentKey: null,
      isAdminPreview: false,
      modules: [],
      currentLesson: null,
      currentModule: null,
      progressByLesson: {},
      ratingByLesson: {},
      watchedByLesson: {},
      expandedModules: {},
      sidebarOpen: false,
      hoverRating: 0,
      ratingSaved: false,
      ratingSaveTimer: null,
    }
  },
  computed: {
    totalLessons() {
      return this.modules.reduce((s, m) => s + (m.lessons ? m.lessons.length : 0), 0)
    },
    completedCount() {
      return this.allLessons.filter(item => this.isDone(item.l.id)).length
    },
    overallPct() {
      if (!this.totalLessons) return 0
      return Math.round((this.completedCount / this.totalLessons) * 100)
    },
    allLessons() {
      const arr = []
      this.modules.forEach(m => (m.lessons || []).forEach(l => arr.push({ l, m })))
      return arr
    },
    firstLesson() { return this.allLessons[0] || null },
    prevLesson() {
      if (!this.currentLesson) return null
      const idx = this.allLessons.findIndex(x => x.l.id === this.currentLesson.id)
      return idx > 0 ? this.allLessons[idx - 1] : null
    },
    nextLesson() {
      if (!this.currentLesson) return null
      const idx = this.allLessons.findIndex(x => x.l.id === this.currentLesson.id)
      return idx >= 0 && idx < this.allLessons.length - 1 ? this.allLessons[idx + 1] : null
    },
    displayRating() {
      if (!this.currentLesson) return 0
      return this.hoverRating || this.ratingByLesson[String(this.currentLesson.id)] || 0
    },
  },
  async mounted() {
    await this.loadAccess()
    window.addEventListener('message', this.onPlayerMsg)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onPlayerMsg)
    if (this.ratingSaveTimer) clearTimeout(this.ratingSaveTimer)
  },
  methods: {
    progressCacheKey() {
      if (!this.ownerUserId || !this.studentKey) return null
      return `academy-progress:${this.ownerUserId}:${this.studentKey}`
    },
    loadLocalProgressCache() {
      try {
        const key = this.progressCacheKey()
        if (!key) return {}
        return JSON.parse(localStorage.getItem(key) || '{}') || {}
      } catch (_) {
        return {}
      }
    },
    saveLocalProgressRow(lessonId, row) {
      try {
        const key = this.progressCacheKey()
        if (!key) return
        const cache = this.loadLocalProgressCache()
        cache[String(lessonId)] = {
          watched_seconds: Number((row && row.watched_seconds) || 0),
          progress_pct: Number((row && row.progress_pct) || 0),
          completed_at: row && row.completed_at ? row.completed_at : null,
        }
        localStorage.setItem(key, JSON.stringify(cache))
      } catch (_) {}
    },
    mergeLocalProgressCache() {
      const cache = this.loadLocalProgressCache()
      Object.keys(cache).forEach((lessonId) => {
        const localRow = cache[lessonId] || {}
        const current = this.progressByLesson[lessonId] || {}
        const merged = {
          ...current,
          watched_seconds: Math.max(Number(current.watched_seconds || 0), Number(localRow.watched_seconds || 0)),
          progress_pct: Math.max(Number(current.progress_pct || 0), Number(localRow.progress_pct || 0)),
          completed_at: current.completed_at || localRow.completed_at || null,
        }
        this.$set(this.progressByLesson, lessonId, merged)
        this.watchedByLesson[lessonId] = Number(merged.watched_seconds || 0)
      })
    },
    resolveFallbackIds() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        const userId = Number(userData.id) || null
        const ownerFromUser = Number(userData.fiId || userData.fi_id || userData.owner_user_id) || null
        return {
          studentKey: userId ? String(userId) : null,
          ownerUserId: ownerFromUser || userId,
        }
      } catch (_) {
        return { studentKey: null, ownerUserId: null }
      }
    },
    async loadCatalogFallback(ownerUserId, studentKey) {
      if (!ownerUserId) return
      const { data } = await publicApi.get(`training/catalog-open/${ownerUserId}`)
      this.ownerUserId = ownerUserId
      this.studentKey = studentKey || String(ownerUserId)
      this.isAdminPreview = true
      this.modules = (data && data.modules) || []
      if (this.modules.length) {
        this.$set(this.expandedModules, this.modules[0].id, true)
      }
      await this.loadProgress()
      this.selectResumeLesson()
    },
    async loadAccess() {
      try {
        const { data } = await api.get('training/client-access')
        this.ownerUserId = Number(data.ownerUserId) || null
        this.studentKey = data.studentKey || null
        this.isAdminPreview = !!data.isAdminPreview
        this.modules = (data && data.modules) || []
        if (this.modules.length) {
          this.$set(this.expandedModules, this.modules[0].id, true)
        }
        await this.loadProgress()
        this.selectResumeLesson()
      } catch (e) {
        console.error('[MyAcademy] loadAccess', e)
        // Fallback para usuários com token vencido/desincronizado no cliente.
        try {
          const { ownerUserId, studentKey } = this.resolveFallbackIds()
          await this.loadCatalogFallback(ownerUserId, studentKey)
        } catch (fallbackErr) {
          console.error('[MyAcademy] loadAccess fallback', fallbackErr)
        }
      } finally {
        this.loading = false
      }
    },
    async loadProgress() {
      if (!this.ownerUserId || !this.studentKey) return
      try {
        const { data } = await publicApi.get(`training/progress-open/${this.ownerUserId}/${encodeURIComponent(this.studentKey)}`)
        const progress = (data && data.progress) || []
        const ratings = (data && data.ratings) || []
        progress.forEach(row => {
          const key = String(row.lesson_id)
          this.$set(this.progressByLesson, key, row)
          this.watchedByLesson[key] = Number(row.watched_seconds || 0)
        })
        ratings.forEach(row => {
          this.$set(this.ratingByLesson, String(row.lesson_id), Number(row.rating || 0))
        })
        this.mergeLocalProgressCache()
      } catch (e) {
        console.error('[MyAcademy] loadProgress', e)
        this.mergeLocalProgressCache()
      }
    },
    toggleModule(id) {
      this.$set(this.expandedModules, id, !this.expandedModules[id])
    },
    goLesson(lesson, module) {
      this.currentLesson = lesson
      this.currentModule = module
      this.sidebarOpen = false
      this.$set(this.expandedModules, module.id, true)
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    selectResumeLesson() {
      if (!this.modules.length || this.currentLesson) return
      const all = this.allLessons
      if (!all.length) return
      const next = all.find(item => !this.isDone(item.l.id))
      const target = next || all[all.length - 1]
      if (!target) return
      this.currentLesson = target.l
      this.currentModule = target.m
      this.$set(this.expandedModules, target.m.id, true)
    },
    isDone(lessonId) {
      const row = this.progressByLesson[String(lessonId)]
      return row && Number(row.progress_pct || 0) >= 100
    },
    lessonPct(lessonId) {
      const row = this.progressByLesson[String(lessonId)]
      return row ? Math.min(100, Number(row.progress_pct || 0)) : 0
    },
    moduleCompletePct(m) {
      if (!m.lessons || !m.lessons.length) return 0
      const done = m.lessons.filter(l => this.isDone(l.id)).length
      return Math.round((done / m.lessons.length) * 100)
    },
    lessonDurationSeconds(lesson) {
      const min = Math.max(0, Number((lesson && lesson.duration_min) || 0))
      return min ? min * 60 : 300
    },
    async toggleLessonDone(lesson) {
      if (!lesson || !this.ownerUserId || !this.studentKey) return
      const key = String(lesson.id)
      const currentlyDone = this.isDone(lesson.id)
      const watched = currentlyDone ? 0 : this.lessonDurationSeconds(lesson)
      const pct = currentlyDone ? 0 : 100
      try {
        await publicApi.post('training/progress-open/set', {
          owner_user_id: this.ownerUserId,
          lesson_id: lesson.id,
          student_key: this.studentKey,
          watched_seconds: watched,
          progress_pct: pct,
          completed: pct >= 100,
        })
        this.watchedByLesson[key] = watched
        const row = this.progressByLesson[key] ? { ...this.progressByLesson[key] } : {}
        row.watched_seconds = watched
        row.progress_pct = pct
        row.completed_at = pct >= 100 ? new Date().toISOString() : null
        this.$set(this.progressByLesson, key, row)
        this.saveLocalProgressRow(key, row)
      } catch (e) {
        console.error('[MyAcademy] toggleLessonDone', e)
        const row = this.progressByLesson[key] ? { ...this.progressByLesson[key] } : {}
        row.watched_seconds = watched
        row.progress_pct = pct
        row.completed_at = pct >= 100 ? new Date().toISOString() : null
        this.$set(this.progressByLesson, key, row)
        this.saveLocalProgressRow(key, row)
      }
    },
    async onPlayerMsg(event) {
      const frame = this.$refs.lessonFrame
      if (!frame || !this.currentLesson) return
      const d = event.data || {}
      if (d.type !== 'vslbooster-analytics') return
      if (String(d.id_vsl || '') !== String(this.currentLesson.id_vsl || '')) return
      await this.saveProgress(d)
    },
    async saveProgress(payload) {
      if (!this.ownerUserId || !this.studentKey) return
      const lessonKey = String(this.currentLesson.id)
      const lastWatched = Number(this.watchedByLesson[lessonKey] || 0)
      let incoming = Math.max(0, Number(
        payload.period_watched
        || payload.watched_seconds
        || payload.video_time
        || payload.abandon_position
        || payload.ptime
        || 0,
      ))
      const pstatusNum = Number(payload.pstatus || 0)
      if (!incoming && pstatusNum === 2) {
        // Some player builds only emit heartbeat status; advance conservatively.
        incoming = lastWatched + 5
      }
      const delta = Math.max(0, incoming - lastWatched)
      const rawDuration = Number(payload.vsl_duration || payload.duration || 0)
      const fallbackDurationSec = Math.max(1, Number(this.currentLesson.duration_min || 0) * 60)
      const durationSec = Math.max(
        1,
        rawDuration > 1000 ? Math.round(rawDuration / 1000) : Math.round(rawDuration || fallbackDurationSec),
      )
      const pct = Math.min(100, Number(((incoming / durationSec) * 100).toFixed(2)))
      const prevPct = Number((this.progressByLesson[lessonKey] && this.progressByLesson[lessonKey].progress_pct) || 0)
      if (!delta && pct <= prevPct) return
      try {
        await publicApi.post('training/progress-open', {
          owner_user_id: this.ownerUserId,
          lesson_id: this.currentLesson.id,
          student_key: this.studentKey,
          watched_seconds_delta: delta,
          progress_pct: pct,
          completed: pct >= 100,
        })
        this.watchedByLesson[lessonKey] = incoming
        const row = this.progressByLesson[lessonKey] ? { ...this.progressByLesson[lessonKey] } : {}
        row.watched_seconds = incoming
        row.progress_pct = Math.max(prevPct, pct)
        row.completed_at = row.progress_pct >= 100 ? (row.completed_at || new Date().toISOString()) : null
        this.$set(this.progressByLesson, lessonKey, row)
        this.saveLocalProgressRow(lessonKey, row)
      } catch (e) {
        console.error('[MyAcademy] saveProgress', e)
      }
    },
    async submitRating(stars) {
      if (!this.currentLesson || !this.ownerUserId) return
      try {
        await publicApi.post('training/rate-lesson', {
          owner_user_id: this.ownerUserId,
          lesson_id: this.currentLesson.id,
          student_key: this.studentKey,
          rating: stars,
        })
        this.$set(this.ratingByLesson, String(this.currentLesson.id), stars)
        this.ratingSaved = true
        if (this.ratingSaveTimer) clearTimeout(this.ratingSaveTimer)
        this.ratingSaveTimer = setTimeout(() => { this.ratingSaved = false }, 2500)
      } catch (e) {
        console.error('[MyAcademy] submitRating', e)
      }
    },
    buildEmbed(vslId) {
      if (!vslId) return `<!doctype html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box}body{background:#0b1020;display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif}div{text-align:center;color:rgba(255,255,255,.4)}p{font-size:13px;margin-top:8px}</style></head><body><div><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="2" y="2" width="20" height="20" rx="3"/><path d="M9.5 8.5l5 3.5-5 3.5V8.5z"/></svg><p>Vídeo não configurado</p></div></body></html>`
      const scriptSrc = `${window.location.origin}/js/booster.min.js`
      const sc = 'script'
      return `<!doctype html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box}body{background:#0b1020}#w{padding:8px;max-width:1000px;margin:0 auto}</style></head><body><div id="w"><div>[vid_${vslId}]</div></div><${sc}>(function(){function q(v){return typeof v==='string'?v:(v&&v.toString?v.toString():'')}function isA(u){return !!u&&(u.indexOf('/analytic')!==-1||u.indexOf('/analytics')!==-1)}function parseBody(b){if(!b)return null;try{if(typeof b==='string'){try{return JSON.parse(b)}catch(e){var o={};b.split('&').forEach(function(kv){var p=kv.split('=');if(p[0])o[decodeURIComponent(p[0])]=decodeURIComponent(p[1]||'')});return o}}if(typeof URLSearchParams!=='undefined'&&b instanceof URLSearchParams){var out={};b.forEach(function(v,k){out[k]=v});return out}if(typeof FormData!=='undefined'&&b instanceof FormData){var f={};b.forEach(function(v,k){f[k]=v});return f}if(typeof Blob!=='undefined'&&b instanceof Blob){return null}}catch(e){}return null}function emit(p){if(p&&p.id_vsl){window.parent&&window.parent.postMessage(Object.assign({type:'vslbooster-analytics'},p),'*')}}try{var _f=window.fetch;window.fetch=function(i,o){try{var u=typeof i==='string'?i:(i&&i.url)||'';if(isA(u)){emit(parseBody(o&&o.body))}}catch(e){}return _f.apply(this,arguments)}}catch(e){}try{var XO=XMLHttpRequest.prototype.open;var XS=XMLHttpRequest.prototype.send;XMLHttpRequest.prototype.open=function(m,u){this.__vslUrl=q(u);return XO.apply(this,arguments)};XMLHttpRequest.prototype.send=function(b){try{if(isA(this.__vslUrl)){emit(parseBody(b))}}catch(e){}return XS.apply(this,arguments)}}catch(e){}try{if(navigator&&navigator.sendBeacon){var _sb=navigator.sendBeacon.bind(navigator);navigator.sendBeacon=function(u,d){try{if(isA(q(u))){if(typeof d==='string'){emit(parseBody(d))}else if(typeof URLSearchParams!=='undefined'&&d instanceof URLSearchParams){emit(parseBody(d))}else if(typeof FormData!=='undefined'&&d instanceof FormData){emit(parseBody(d))}}}catch(e){}return _sb(u,d)}}}catch(e){}})();</${sc}><${sc} src="${scriptSrc}" type="module" async></${sc}></body></html>`
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; }
.as-root {
  min-height: 100vh;
  background: #0b0f1a;
  color: #e2e8f0;
  font-family: 'Inter', system-ui, sans-serif;
  display: flex;
  flex-direction: column;
}
.as-header {
  height: 56px;
  background: #0d1117;
  border-bottom: 1px solid rgba(255,255,255,.07);
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 20px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
}
.as-back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: rgba(255,255,255,.5);
  font-size: 13px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 7px;
  flex-shrink: 0;
  transition: background .15s, color .15s;
}
.as-back-btn:hover { background: rgba(255,255,255,.08); color: #fff; }
.as-header-brand { flex-shrink: 0; }
.as-brand-name { font-size: 14px; font-weight: 700; color: #fff; white-space: nowrap; }
.as-header-progress {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.as-progress-text { font-size: 12px; color: rgba(255,255,255,.5); white-space: nowrap; flex-shrink: 0; }
.as-progress-track { flex: 1; height: 5px; background: rgba(255,255,255,.1); border-radius: 99px; overflow: hidden; min-width: 60px; }
.as-progress-fill { height: 100%; background: linear-gradient(90deg, #E86A10, #F5B935); border-radius: 99px; transition: width .4s ease; }
.as-progress-pct { font-size: 12px; font-weight: 700; color: #E86A10; flex-shrink: 0; }
.as-menu-toggle { display: none; background: none; border: none; color: rgba(255,255,255,.6); cursor: pointer; padding: 6px; border-radius: 6px; flex-shrink: 0; }
.as-menu-toggle:hover { background: rgba(255,255,255,.08); color: #fff; }
@media (max-width: 900px) { .as-menu-toggle { display: flex; } }
.as-body { flex: 1; display: flex; overflow: hidden; }
.as-overlay { position: fixed; inset: 0; z-index: 49; background: rgba(0,0,0,.5); }
.as-sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #0d1117;
  border-right: 1px solid rgba(255,255,255,.07);
  overflow-y: auto;
  height: calc(100vh - 56px);
  position: sticky;
  top: 56px;
}
.as-sidebar-inner { padding: 10px 8px 60px; }
.as-sidebar-loading, .as-sidebar-empty { padding: 20px 12px; color: rgba(255,255,255,.4); font-size: 13px; }
@media (max-width: 900px) {
  .as-sidebar { position: fixed; top: 56px; left: 0; height: calc(100vh - 56px); z-index: 50; transform: translateX(-100%); transition: transform .25s ease; }
  .as-sidebar--open { transform: translateX(0); }
}
.as-mod { margin-bottom: 4px; }
.as-mod-hd { width: 100%; display: flex; align-items: center; gap: 6px; background: none; border: none; color: rgba(255,255,255,.8); font-size: 12px; font-weight: 600; padding: 8px 10px; border-radius: 8px; cursor: pointer; text-align: left; transition: background .12s; }
.as-mod-hd:hover { background: rgba(255,255,255,.06); }
.as-chevron { flex-shrink: 0; transform: rotate(0deg); transition: transform .2s; }
.as-chevron--open { transform: rotate(90deg); }
.as-mod-name { flex: 1; }
.as-mod-pct { font-size: 10px; color: rgba(255,255,255,.35); font-weight: 400; }
.as-mod-pct--done { color: #28C76F; }
.as-lesson-list { padding: 2px 0 4px 20px; }
.as-lesson-btn { width: 100%; display: flex; align-items: center; gap: 7px; background: none; border: none; color: rgba(255,255,255,.6); font-size: 12px; padding: 7px 10px; border-radius: 7px; cursor: pointer; text-align: left; transition: background .12s, color .12s; }
.as-lesson-btn:hover { background: rgba(255,255,255,.06); color: rgba(255,255,255,.9); }
.as-lesson-btn--active { background: rgba(232,106,16,.15); color: #E86A10; }
.as-lesson-btn--done { color: rgba(40,199,111,.7); }
.as-lesson-ico { flex-shrink: 0; width: 16px; display: flex; align-items: center; justify-content: center; }
.as-lesson-name { flex: 1; line-height: 1.3; }
.as-lesson-dur { font-size: 10px; color: rgba(255,255,255,.3); flex-shrink: 0; }
.as-lesson-flag {
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.04);
  color: rgba(255,255,255,.72);
  border-radius: 999px;
  font-size: 10px;
  padding: 3px 8px;
  cursor: pointer;
  flex-shrink: 0;
}
.as-lesson-flag--on {
  border-color: rgba(40,199,111,.7);
  background: rgba(40,199,111,.14);
  color: #28C76F;
}
.as-main { flex: 1; overflow-y: auto; padding: 0; min-width: 0; }
.as-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: calc(100vh - 56px); gap: 12px; color: rgba(255,255,255,.4); }
.as-spinner { width: 32px; height: 32px; border: 3px solid rgba(255,255,255,.1); border-top-color: #E86A10; border-radius: 50%; animation: as-spin .8s linear infinite; }
@keyframes as-spin { to { transform: rotate(360deg); } }
.as-welcome { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: calc(100vh - 56px); text-align: center; padding: 40px 20px; gap: 16px; }
.as-welcome-icon { width: 80px; height: 80px; background: rgba(232,106,16,.1); border-radius: 20px; display: flex; align-items: center; justify-content: center; }
.as-welcome h1 { font-size: 22px; font-weight: 700; color: #fff; }
.as-welcome p { font-size: 14px; color: rgba(255,255,255,.5); }
.as-start-btn { display: flex; align-items: center; gap: 8px; background: #E86A10; color: #fff; border: none; border-radius: 10px; padding: 12px 24px; font-size: 14px; font-weight: 600; cursor: pointer; margin-top: 8px; transition: background .15s; }
.as-start-btn:hover { background: #d4600e; }
.as-lesson-wrap { max-width: 900px; margin: 0 auto; padding: 28px 24px 60px; }
.as-lesson-head { margin-bottom: 16px; }
.as-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(255,255,255,.35); margin-bottom: 10px; }
.as-lesson-title { font-size: 22px; font-weight: 700; color: #fff; margin-bottom: 10px; line-height: 1.3; }
.as-lesson-badges { display: flex; flex-wrap: wrap; gap: 8px; }
.as-badge { display: flex; align-items: center; gap: 5px; font-size: 11px; background: rgba(255,255,255,.07); color: rgba(255,255,255,.6); padding: 4px 10px; border-radius: 99px; }
.as-badge--progress { color: #E86A10; background: rgba(232,106,16,.12); }
.as-badge--done { color: #28C76F; background: rgba(40,199,111,.12); }
.as-player-wrap { position: relative; width: 100%; padding-bottom: 56.25%; margin: 20px 0; background: #0b0f1a; border-radius: 12px; overflow: hidden; }
.as-player { position: absolute; inset: 0; width: 100%; height: 100%; border: none; border-radius: 12px; }
.as-lesson-desc { background: rgba(255,255,255,.04); border-radius: 10px; padding: 16px 18px; margin-bottom: 20px; font-size: 14px; color: rgba(255,255,255,.7); line-height: 1.6; }
.as-rating-card { background: rgba(255,255,255,.04); border-radius: 12px; padding: 18px 20px; margin-bottom: 24px; display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
.as-rating-label { font-size: 13px; color: rgba(255,255,255,.6); }
.as-stars { display: flex; gap: 4px; }
.as-star { background: none; border: none; font-size: 26px; color: rgba(255,255,255,.2); cursor: pointer; padding: 2px; transition: color .12s, transform .12s; line-height: 1; }
.as-star:hover, .as-star--on { color: #F5B935; transform: scale(1.1); }
.as-rating-saved { font-size: 12px; color: #28C76F; }
.as-fade-enter-active, .as-fade-leave-active { transition: opacity .3s; }
.as-fade-enter, .as-fade-leave-to { opacity: 0; }
.as-nav { display: flex; justify-content: space-between; align-items: center; padding-top: 8px; border-top: 1px solid rgba(255,255,255,.07); }
.as-nav-btn { display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1); color: rgba(255,255,255,.7); border-radius: 10px; padding: 10px 18px; font-size: 13px; font-weight: 500; cursor: pointer; transition: background .15s, color .15s; }
.as-nav-btn:hover { background: rgba(255,255,255,.1); color: #fff; }
.as-nav-btn--primary { background: #E86A10; border-color: #E86A10; color: #fff; }
.as-nav-btn--primary:hover { background: #d4600e; }
.as-manual-complete { margin-top: 16px; }
.as-manual-complete__btn {
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.14);
  color: rgba(255,255,255,.85);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 12px;
  cursor: pointer;
}
.as-manual-complete__btn:hover { background: rgba(255,255,255,.1); }
.as-preview-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(232,106,16,0.12);
  border-bottom: 1px solid rgba(232,106,16,0.3);
  padding: 9px 20px;
  font-size: 12px;
  font-weight: 500;
  color: #F5B935;
  flex-shrink: 0;
}
.as-preview-manage {
  margin-left: auto;
  background: rgba(232,106,16,0.2);
  border: 1px solid rgba(232,106,16,0.4);
  color: #E86A10;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background .15s;
}
.as-preview-manage:hover { background: rgba(232,106,16,0.35); }
</style>
